import useUpdateAddressPropertyInfo from '@/api/mutations/addresses/useUpdateAddressPropertyInfo';
import useCreateLinkedSpruceProject from '@/api/mutations/project/usePushProjectToSpruce';
import type { PropertyInformation } from '@/gql/graphql';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Field,
  Fieldset,
  Form,
  Input,
  Modal,
  Select,
} from '@payaca/ambrosia';
import { FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  BUILT_FORM_OPTIONS,
  CONSTRUCTION_AGE_BAND_OPTIONS,
  getWallInsulationOptions,
  HEATING_SYSTEM_OPTIONS,
  isValidInsulationForConstruction,
  PROPERTY_TYPE_OPTIONS,
  WALL_CONSTRUCTION_OPTIONS,
  WINDOW_GLAZING_OPTIONS,
} from './propertyInfoOptions';
import { PROPERTY_INFO_SCHEMA, PropertyInfoForm } from './propertyInfoSchema';

type PropertyInfoStepProps = {
  addressId: string;
  projectId: string;
  propertyInfo?: Pick<
    PropertyInformation,
    | 'propertyType'
    | 'builtForm'
    | 'constructionAgeBand'
    | 'floorAreaM2'
    | 'numBedrooms'
    | 'heatingSystem'
    | 'wallConstruction'
    | 'wallInsulation'
    | 'windowGlazingType'
    | 'loftInsulationMm'
    | 'numStoreys'
  >;
  onClose: () => void;
  onSuccess: () => void;
  onError: (message: string) => void;
};

export const PropertyInfoStep: FC<PropertyInfoStepProps> = (props) => {
  const { addressId, projectId, propertyInfo, onSuccess, onError } = props;
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { mutateAsync: updatePropertyInfo, isPending: isUpdating } =
    useUpdateAddressPropertyInfo({ meta: { skipGlobalErrorToast: true } });

  const { mutateAsync: pushToSpruce, isPending: isPushing } =
    useCreateLinkedSpruceProject({
      projectId,
      options: { meta: { skipGlobalErrorToast: true } },
    });

  const formMethods = useForm<PropertyInfoForm>({
    mode: 'all',
    resolver: zodResolver(PROPERTY_INFO_SCHEMA),
    defaultValues: {
      propertyType: propertyInfo?.propertyType ?? undefined,
      builtForm: propertyInfo?.builtForm ?? undefined,
      constructionAgeBand: propertyInfo?.constructionAgeBand ?? undefined,
      floorAreaM2:
        propertyInfo?.floorAreaM2 != null
          ? String(propertyInfo.floorAreaM2)
          : '',
      numBedrooms:
        propertyInfo?.numBedrooms != null
          ? String(propertyInfo.numBedrooms)
          : '',
      heatingSystem: propertyInfo?.heatingSystem ?? undefined,
      wallConstruction: propertyInfo?.wallConstruction ?? undefined,
      wallInsulation: propertyInfo?.wallInsulation ?? undefined,
      windowGlazingType: propertyInfo?.windowGlazingType ?? undefined,
      loftInsulationMm:
        propertyInfo?.loftInsulationMm != null
          ? String(propertyInfo.loftInsulationMm)
          : '',
      numStoreys:
        propertyInfo?.numStoreys != null
          ? String(propertyInfo.numStoreys)
          : undefined,
    },
  });

  const isProcessing = isUpdating || isPushing;

  const handleSubmit = async (data: PropertyInfoForm) => {
    setErrorMessage(null);

    try {
      await updatePropertyInfo({
        addressId,
        propertyType: data.propertyType,
        builtForm: data.builtForm,
        constructionAgeBand: data.constructionAgeBand || undefined,
        floorAreaM2: parseFloat(data.floorAreaM2),
        numBedrooms: parseInt(data.numBedrooms, 10),
        heatingSystem: data.heatingSystem,
        wallConstruction: data.wallConstruction,
        wallInsulation: data.wallInsulation,
        windowGlazingType: data.windowGlazingType,
        loftInsulationMm: parseInt(data.loftInsulationMm, 10),
        numStoreys: data.numStoreys ? parseInt(data.numStoreys, 10) : undefined,
      });

      const response = await pushToSpruce({ projectId });
      const result = response.createLinkedSpruceProject;

      if (result.__typename === 'CreateLinkedProjectError') {
        setErrorMessage(result.message);
        onError(result.message);
      } else {
        onSuccess();
      }
    } catch {
      const message = 'Something went wrong pushing to Spruce';
      setErrorMessage(message);
      onError(message);
    }
  };

  const watchedWallConstruction = formMethods.watch('wallConstruction');

  return (
    <Form methods={formMethods} onSubmit={(data) => void handleSubmit(data)}>
      <Modal.Body>
        <Fieldset>
          <Controller<PropertyInfoForm, 'propertyType'>
            name="propertyType"
            render={({ field: { name, ...attributes } }) => (
              <Field name={name}>
                <Field.Label>Property type *</Field.Label>
                <Select
                  multiple={false}
                  options={[...PROPERTY_TYPE_OPTIONS]}
                  {...attributes}
                />
              </Field>
            )}
          />

          <Controller<PropertyInfoForm, 'builtForm'>
            name="builtForm"
            render={({ field: { name, ...attributes } }) => (
              <Field name={name}>
                <Field.Label>Built form *</Field.Label>
                <Select
                  multiple={false}
                  options={[...BUILT_FORM_OPTIONS]}
                  {...attributes}
                />
              </Field>
            )}
          />

          <Controller<PropertyInfoForm, 'constructionAgeBand'>
            name="constructionAgeBand"
            render={({ field: { name, ...attributes } }) => (
              <Field name={name}>
                <Field.Label>Construction age band</Field.Label>
                <Select
                  multiple={false}
                  options={[...CONSTRUCTION_AGE_BAND_OPTIONS]}
                  {...attributes}
                />
              </Field>
            )}
          />

          <Controller<PropertyInfoForm, 'floorAreaM2'>
            name="floorAreaM2"
            render={({ field: { name, value, onChange, onBlur, ref } }) => (
              <Field name={name}>
                <Field.Label>Floor area (m2) *</Field.Label>
                <Input
                  type="number"
                  value={value ?? ''}
                  onChange={onChange}
                  onBlur={onBlur}
                  ref={ref}
                />
              </Field>
            )}
          />

          <Controller<PropertyInfoForm, 'numBedrooms'>
            name="numBedrooms"
            render={({ field: { name, value, onChange, onBlur, ref } }) => (
              <Field name={name}>
                <Field.Label>Number of bedrooms *</Field.Label>
                <Input
                  type="number"
                  value={value ?? ''}
                  onChange={onChange}
                  onBlur={onBlur}
                  ref={ref}
                />
              </Field>
            )}
          />

          <Controller<PropertyInfoForm, 'heatingSystem'>
            name="heatingSystem"
            render={({ field: { name, ...attributes } }) => (
              <Field name={name}>
                <Field.Label>Heating system *</Field.Label>
                <Select
                  multiple={false}
                  options={[...HEATING_SYSTEM_OPTIONS]}
                  {...attributes}
                />
              </Field>
            )}
          />

          <Controller<PropertyInfoForm, 'wallConstruction'>
            name="wallConstruction"
            render={({ field: { name, value, onChange, ...attributes } }) => (
              <Field name={name}>
                <Field.Label>Wall construction *</Field.Label>
                <Select
                  multiple={false}
                  options={[...WALL_CONSTRUCTION_OPTIONS]}
                  value={value}
                  onChange={(newValue) => {
                    onChange(newValue);
                    // Clear wall insulation if it's no longer valid for the new construction type
                    const currentInsulation =
                      formMethods.getValues('wallInsulation');
                    if (
                      !isValidInsulationForConstruction(
                        currentInsulation,
                        newValue
                      )
                    ) {
                      formMethods.resetField('wallInsulation', {
                        defaultValue: undefined,
                      });
                    }
                  }}
                  {...attributes}
                />
              </Field>
            )}
          />

          <Controller<PropertyInfoForm, 'wallInsulation'>
            name="wallInsulation"
            render={({ field: { name, ...attributes } }) => (
              <Field name={name}>
                <Field.Label>Wall insulation *</Field.Label>
                <Select
                  multiple={false}
                  options={getWallInsulationOptions(watchedWallConstruction)}
                  {...attributes}
                />
              </Field>
            )}
          />

          <Controller<PropertyInfoForm, 'windowGlazingType'>
            name="windowGlazingType"
            render={({ field: { name, ...attributes } }) => (
              <Field name={name}>
                <Field.Label>Window glazing type *</Field.Label>
                <Select
                  multiple={false}
                  options={[...WINDOW_GLAZING_OPTIONS]}
                  {...attributes}
                />
              </Field>
            )}
          />

          <Controller<PropertyInfoForm, 'loftInsulationMm'>
            name="loftInsulationMm"
            render={({ field: { name, value, onChange, onBlur, ref } }) => (
              <Field name={name}>
                <Field.Label>Loft insulation (mm) *</Field.Label>
                <Input
                  type="number"
                  value={value ?? ''}
                  onChange={onChange}
                  onBlur={onBlur}
                  ref={ref}
                />
              </Field>
            )}
          />

          <Controller<PropertyInfoForm, 'numStoreys'>
            name="numStoreys"
            render={({ field: { name, value, onChange, onBlur, ref } }) => (
              <Field name={name}>
                <Field.Label>Number of storeys (optional)</Field.Label>
                <Input
                  type="number"
                  value={value ?? ''}
                  onChange={onChange}
                  onBlur={onBlur}
                  ref={ref}
                />
              </Field>
            )}
          />
        </Fieldset>

        {errorMessage && (
          <Alert colour="red" className="mt-4">
            {errorMessage}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button
            type="submit"
            isProcessing={isProcessing}
            disabled={isProcessing}
          >
            Push to Spruce
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Form>
  );
};
